import Vue from "vue";
import App from "./App.vue";
import Vuetify from "vuetify";
import "vuetify/dist/vuetify.min.css";
import router from "./router/router";
import store from "./plugins/vuex";
import axios from "./plugins/axios.js";
import Toast, { POSITION } from "vue-toastification";
import "vue-toastification/dist/index.css";
import VueTimepicker from "vue2-timepicker";
import "vue2-timepicker/dist/VueTimepicker.css";
import wysiwyg from "vue-wysiwyg";
import "vue-wysiwyg/dist/vueWysiwyg.css";
import "@/plugins/dayjs";
import Pusher from "pusher-js";
import Echo from "laravel-echo";
import "boxicons/css/boxicons.min.css";
import VueVirtualScroller from 'vue-virtual-scroller';
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';
// Import tour styling
import "@/assets/css/tour.css";

Vue.component("vue-timepicker", VueTimepicker);

import BlankLayout from "./layouts/BlankLayout.vue";
import DefaultLayout from "./layouts/DefaultLayout.vue";
Vue.component("default-layout", DefaultLayout);
Vue.component("blank-layout", BlankLayout);
Vue.config.productionTip = false;

Vue.use(Vuetify);
Vue.use(axios);
Vue.use(VueVirtualScroller);
Vue.use(Toast, {
  transition: "Vue-Toastification__bounce",
  maxToasts: 20,
  newestOnTop: true,
  position: POSITION.BOTTOM_LEFT,
});
let wysiwyg_config = {
  hideModules: { removeFormat: true, code: true },
};
Vue.use(wysiwyg, wysiwyg_config);

Pusher.logToConsole = false;

// Create a method to get the current auth token
function getAuthToken() {
  try {
    // Try multiple possible token sources
    const sources = [
      () => {
        const vuexData = localStorage.getItem("vuex");
        if (vuexData) {
          const parsed = JSON.parse(vuexData);
          return parsed?.auth?.token || parsed?.token;
        }
        return null;
      },
      () => localStorage.getItem('token'),
      () => sessionStorage.getItem('token'),
      () => {
        // If using Vuex store directly
        if (window.app && window.app.$store) {
          return window.app.$store.state.auth?.token;
        }
        return null;
      }
    ];

    for (const source of sources) {
      const token = source();
      if (token && token.length > 10) { // Basic validation
        console.log('[Echo] Token found via source:', source.name || 'anonymous');
        return token;
      }
    }
    
    console.warn('[Echo] No valid auth token found');
    return null;
  } catch (e) {
    console.error('[Echo] Error retrieving auth token:', e);
    return null;
  }
}

// Initialize Echo with simpler configuration
const echoConfig = {
  broadcaster: 'pusher',
  key: process.env.VUE_APP_PUSHER_KEY,
  cluster: process.env.VUE_APP_PUSHER_CLUSTER,
  forceTLS: true,
  authEndpoint: (process.env.VUE_APP_API_URL || "").replace(/\/$/, "") + "/api/broadcasting/auth",
  auth: {
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
  },
  authorizer: (channel, options) => {
    return {
      authorize: (socketId, callback) => {
        const token = getAuthToken();
        
        if (!token) {
          console.error('[Echo] No auth token available for channel:', channel.name);
          callback(new Error('No authentication token'), null);
          return;
        }

        console.log('[Echo] Authorizing channel:', channel.name);
        
        // Use standard fetch with JSON (not form data)
        const authData = {
          socket_id: socketId,
          channel_name: channel.name
        };

        fetch(options.authEndpoint, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(authData),
        })
        .then(response => {
          console.log('[Echo] Auth response status:', response.status);
          
          if (!response.ok) {
            return response.text().then(text => {
              console.error('[Echo] Auth failed:', response.status, text);
              throw new Error(`Authorization failed: ${response.status} - ${text}`);
            });
          }
          
          return response.json();
        })
        .then(data => {
          console.log('[Echo] Authorization successful for:', channel.name);
          callback(null, data);
        })
        .catch(error => {
          console.error('[Echo] Authorization error:', error);
          callback(error, null);
        });
      },
    };
  },
};

// Create Echo instance
window.Echo = new Echo(echoConfig);

// Vue plugin to add Echo to Vue instance
const EchoPlugin = {
  install(Vue) {
    Vue.prototype.$echo = window.Echo;
    Vue.echo = window.Echo;
  }
};

Vue.use(EchoPlugin);

// Router navigation guard for role-based access control
router.beforeEach((to, from, next) => {
  // Get user from Vuex store
  const user = store.state.auth.user;
  
  // Check if route requires specific role
  if (to.meta.requiresRole && Array.isArray(to.meta.requiresRole)) {
    // If user is not logged in, redirect to login
    if (!user || !user.role_id) {
      console.warn('[Router Guard] User not authenticated, redirecting to login');
      return next({ path: '/login' });
    }
    
    // Check if user's role is in the required roles (or an explicit per-route user exception)
    const allowedUserIds = to.meta.allowedUserIds || [];
    // Same exceptions as the guard in router/router.js: extra roles via
    // role_members, and routes open to managers (managed users or an
    // admin-picked Time Tracking manager).
    const hasMemberRole = (user.role_members || []).some(m => to.meta.requiresRole.includes(m.user_role_id));
    const isManager = to.meta.requiresManagesUsers
      && ((user.managed_users && user.managed_users.length > 0) || !!user.time_tracking_manager);
    if (!to.meta.requiresRole.includes(user.role_id) && !allowedUserIds.includes(user.id) && !hasMemberRole && !isManager) {
      console.warn(`[Router Guard] Access denied. User role ${user.role_id} not in required roles`);
      // Redirect to dashboard with error message
      store.commit('auth/setMessage', {
        show: true,
        message: 'You do not have permission to access this page'
      });
      return next({ path: '/dashboard' });
    }
  }
  
  // Allow navigation
  next();
});

new Vue({
  router,
  store,
  vuetify: new Vuetify(),
  render: (h) => h(App),
}).$mount("#app");
